import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // EmailJS configuration
      const templateParams = {
        to_email: 'hello@jamelly.art',
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
      };

      const result = await emailjs.send(
        'service_zvr9wom',
        'template_27dxh3c',
        templateParams,
        'Ffrz1ll3ZialRBEXe'
      );

      if (result.status === 200) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        // Google Analytics event
        if (window.gtag) {
          window.gtag('event', 'contact_form_submit', {
            event_category: 'engagement',
            event_label: 'Contact Form',
            name: formData.name,
            email: formData.email
          });
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    
    // Debug: Check if environment variables are loaded
    console.log('API Key:', process.env.REACT_APP_BREVO_API_KEY ? 'Loaded' : 'Missing');
    console.log('List ID:', process.env.REACT_APP_BREVO_LIST_ID ? 'Loaded' : 'Missing');
    
    try {
      // Brevo API integration
      const requestBody = {
        email: newsletterEmail,
        listIds: [parseInt(process.env.REACT_APP_BREVO_LIST_ID)],
        updateEnabled: true,
      };
      
      console.log('Request body:', requestBody);
      
      const response = await fetch('https://api.brevo.com/v3/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_BREVO_API_KEY,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Success response:', result);
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } else {
        const errorData = await response.json();
        console.error('Brevo API error:', errorData);
        setNewsletterStatus('error');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setNewsletterStatus('error');
    }
    
    // Reset status after 5 seconds
    setTimeout(() => {
      setNewsletterStatus(null);
    }, 5000);
  };

  return (
    <div className="contact">
      <SEO 
        title="Contact"
        description="I'd love to hear from you. Whether you have a question about my work, want to share how a piece has touched you, or just want to connect, please reach out."
        image="/images/site-photos/BR1A2199.JPG"
        url="/contact"
      />
      <div className="container">
        {/* Header Quote with Background */}
        <section className="contact-header">
          <div className="header-background">
            <img 
              src="/images/site-photos/BR1A2199.JPG" 
              alt="Contact background" 
              className="header-bg-image"
            />
            <div className="header-overlay"></div>
          </div>
          <div className="header-quote">
            <p>Reach out if you've felt something.</p>
          </div>
        </section>

        {/* Contact Form with Photo */}
        <section className="contact-form-section">
          <div className="contact-layout">
            <div className="contact-photo">
              <img 
                src="/images/site-photos/BR1A0093.JPG" 
                alt="Contact" 
                className="contact-image"
              />
            </div>
            <div className="form-container">
            <h1 className="contact-title">CONTACT <span className="brick-font">Me</span></h1>
            <p className="contact-subtitle">
              I'd love to hear from you. Whether you have a question about my work, 
              want to share how a piece has touched you, or just want to connect, 
              please reach out.
            </p>

            {submitStatus === 'success' && (
              <div className="success-message">
                <p>Thank you for your message. I'll get back to you soon.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message">
                <p>Sorry, there was an error sending your message. Please try again or email me directly at hello@jamelly.art</p>
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="6"
                  required
                  placeholder="Share your thoughts, questions, or how my work has touched you..."
                ></textarea>
              </div>

              <button 
                type="submit" 
                className={`btn btn-accent ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
            </form>
          </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="newsletter-section">
          <div className="newsletter-content">
            <h2 className="newsletter-title">Stay Connected</h2>
            <p className="newsletter-text">
              Subscribe to my newsletter for updates on new work, behind-the-scenes stories, and creative inspiration.
            </p>
            
            {newsletterStatus === 'success' && (
              <div className="success-message">
                <p>Thank you for subscribing! You'll receive updates about new work and creative inspiration.</p>
              </div>
            )}

            {newsletterStatus === 'error' && (
              <div className="error-message">
                <p>Sorry, there was an error with your subscription. Please try again.</p>
              </div>
            )}

            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="newsletter-input"
                required
              />
              <button 
                type="submit" 
                className={`btn ${newsletterStatus === 'submitting' ? 'submitting' : ''}`}
                disabled={newsletterStatus === 'submitting'}
              >
                {newsletterStatus === 'submitting' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </section>

        {/* Additional Contact Info */}
        <section className="contact-info">
          <div className="info-grid">
            <div className="info-item">
              <h3 className="info-title">Instagram</h3>
              <a 
                href="https://instagram.com/jamellyyyy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="info-link"
              >
                @jamellyyyy
              </a>
            </div>
            
            <div className="info-item">
              <h3 className="info-title">Studio Email</h3>
              <a 
                href="mailto:hello@jamelly.art" 
                className="info-link"
              >
                hello@jamelly.art
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact; 